import type {ReactElement} from 'react';
import React from 'react';
import {getCurrentPageHashedID, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ColorFillPropertyType} from '@Components/color-fill';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import type {ColorBackgroundObject} from '@PosterWhiteboard/page/background/color-background.class';
import {getColorFillTypeForBackgroundDropdownItem, getFillColorForBackgroundDropdownItem} from '@PosterWhiteboard/page/background/color-background.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';
import {
  onOpenStockBackgroundModal,
  onOpenStockVideoBackgroundModal,
  onOpenUserBackgroundUploadsModal,
  onOpenUserImageBackgroundUploadsModal,
  onOpenUserVideoBackgroundUploadsModal,
} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ContextualMenuImageThumbnailOption} from '@Components/contextual-menu/components/contextual-menu-image-thumbnail-option';
import type {VideoBackgroundObject} from '@PosterWhiteboard/page/background/video-background.class';
import {repoVideoThumbImageURL, UserVideoSource} from '@Libraries/user-video-library';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {PageBackgroundDropDownItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option/page-background-option.types';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useAppSelector} from '@/hooks';
import {getUrlForImageItemBigTierScreenSize, ImageItemSource} from '@/libraries/image-item.library';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItem7} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {PageBackgroundFlyoutOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option/components/page-background-flyout-option/page-background-flyout-option';
import styles from './page-background-option.module.scss';

interface PageBackgroundOptionProps {
  onClickColorFill(): void;

  onClickEditImageBackground(): void;

  onClickEditVideoBackground(): void;
}

export function PageBackgroundOption({onClickColorFill, onClickEditImageBackground, onClickEditVideoBackground}: PageBackgroundOptionProps): ReactElement | null {
  const currentPageHashedID = useAppSelector(getCurrentPageHashedID);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  if (!currentBackgroundObject || !currentPageHashedID) {
    return null;
  }
  const currentPage = window.posterEditor?.whiteboard?.pages.getPage(currentPageHashedID);
  if (!currentPage) {
    return null;
  }

  const getBackgroundDropdownItemForBackgroundObject = (): PageBackgroundDropDownItem => {
    switch (currentBackgroundObject.details.type) {
      case BackgroundTypeName.COLOR:
        return getBackgroundDropdownItemForColorBackgroundObject(currentBackgroundObject.details);
      case BackgroundTypeName.IMAGE:
        return getBackgroundDropdownItemForImageBackgroundObject(currentBackgroundObject.details);
      case BackgroundTypeName.VIDEO:
        return getBackgroundDropdownItemForVideoBackgroundObject(currentBackgroundObject.details);
      default:
        throw new Error(`Unhandled backgroundDropDownItem`);
    }
  };

  const getBackgroundDropdownItemForImageBackgroundObject = (imageBackgroundObject: ImageBackgroundObject): PageBackgroundDropDownItem => {
    if (imageBackgroundObject.type === BackgroundTypeName.IMAGE) {
      const {imageSource} = imageBackgroundObject;
      if (imageSource === ImageItemSource.UPLOAD || imageSource === ImageItemSource.BACKGROUND_UPLOAD || imageSource === ImageItemSource.EMAIL_UPLOAD) {
        return PageBackgroundDropDownItem.MY_IMAGES;
      }
      return PageBackgroundDropDownItem.STOCK_IMAGE;
    }

    throw new Error(`Function getBackgroundDropdownItemForImageBackgroundObject expected image background object type to be of image, instead got ${imageBackgroundObject.type}`);
  };

  const getBackgroundDropdownItemForVideoBackgroundObject = (videoBackgroundObject: VideoBackgroundObject): PageBackgroundDropDownItem => {
    if (videoBackgroundObject.videoSource === UserVideoSource.UPLOAD) {
      return PageBackgroundDropDownItem.MY_VIDEOS;
    }
    return PageBackgroundDropDownItem.STOCK_VIDEO;
  };

  const getBackgroundDropdownItemForColorBackgroundObject = (colorBackgroundObject: ColorBackgroundObject): PageBackgroundDropDownItem => {
    switch (colorBackgroundObject.fill.fillType) {
      case FillTypes.SOLID:
        return PageBackgroundDropDownItem.SOLID;
      case FillTypes.LINEAR_GRADIENT:
        return PageBackgroundDropDownItem.LINEAR_GRADIENT;
      case FillTypes.RADIAL_GRADIENT:
        return PageBackgroundDropDownItem.RADIAL_GRADIENT;
      case FillTypes.NONE:
        return PageBackgroundDropDownItem.TRANSPARENT;
      default:
        throw new Error(`currentBackgroundObject fill type: ${colorBackgroundObject.fill.fillType}`);
    }
  };

  const showSubOptionForColor = (): boolean => {
    return currentBackgroundObject.details.type === BackgroundTypeName.COLOR && currentBackgroundObject.details.fill.fillType !== FillTypes.NONE;
  };

  const showSubOptionForImage = (): boolean => {
    return currentBackgroundObject.details.type === BackgroundTypeName.IMAGE;
  };

  const showSubOptionForVideo = (): boolean => {
    return currentBackgroundObject.details.type === BackgroundTypeName.VIDEO;
  };

  const getColorFillType = (): ColorFillPropertyType => {
    if (currentBackgroundObject.details.type !== BackgroundTypeName.COLOR) {
      throw new Error('Current background is not of type color');
    }

    if (currentBackgroundObject.details.fill.fillType === FillTypes.LINEAR_GRADIENT) {
      return ColorFillPropertyType.LINEAR;
    }

    if (currentBackgroundObject.details.fill.fillType === FillTypes.RADIAL_GRADIENT) {
      return ColorFillPropertyType.RADIAL;
    }

    return ColorFillPropertyType.SOLID;
  };

  const getDataForColor = (): ColorFillStorage[] => {
    if (currentBackgroundObject.details.type !== BackgroundTypeName.COLOR) {
      return [];
    }

    return [
      {
        id: 'color-background',
        color: getColorObjectArrayFromFillColor(currentBackgroundObject.details.fill.fillColor),
        fillType: getColorFillType(),
      },
    ];
  };

  const getImgSrcForBackground = (): string => {
    if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
      if (currentBackgroundObject.details.hashedFilename && currentBackgroundObject.details.imageSource) {
        return getUrlForImageItemBigTierScreenSize(
          currentBackgroundObject.details.hashedFilename,
          currentBackgroundObject.details.imageSource
        );
      }
      if (currentBackgroundObject.details.uploadingImageData?.dataUrl) {
        return currentBackgroundObject.details.uploadingImageData.dataUrl;
      }
      throw new Error('Neither hashedFilename+Extension nor dataUrl is present');
    }
    throw new Error('Unable to get image source for image background since background type is not IMAGE');
  };

  const getImgSrcForVideoBackground = (): string => {
    const details = currentBackgroundObject.details as VideoBackgroundObject;
    const {hashedFilename, fileExtension} = details;
    if (hashedFilename && fileExtension) {
      return repoVideoThumbImageURL(hashedFilename, fileExtension);
    }
    throw new Error('Unable to get thumbnail source for video background: hashedFilename or fileExtension is missing');
  };

  const onEditVideoBackground = (): void => {
    onClickEditVideoBackground();
  };

  const getSubOptionForBackgroundOption = (): ReactElement<any> | null => {
    if (showSubOptionForColor()) {
      return <ContextualMenuColorFillOption id="solid-background-color-fill" label={window.i18next.t('pmwjs_color')} data={getDataForColor()} onClick={onClickColorFill} />;
    }
    if (showSubOptionForImage()) {
      return (
        <ContextualMenuImageThumbnailOption
          id="image-background-thumbnail-option"
          label={window.i18next.t('pmwjs_photo')}
          imgSrc={getImgSrcForBackground()}
          onEdit={onClickEditImageBackground}
        />
      );
    }
    if (showSubOptionForVideo()) {
      return (
        <ContextualMenuImageThumbnailOption
          id="video-background-thumbnail-option"
          label={window.i18next.t('pmwjs_video')}
          imgSrc={getImgSrcForVideoBackground()}
          onEdit={onEditVideoBackground}
        />
      );
    }
    return null;
  };

  const onClickHandler = (clickedId: PageBackgroundDropDownItem): void => {
    if (isBackgroundTypeColor(clickedId) || clickedId === PageBackgroundDropDownItem.TRANSPARENT) {
      void currentPage.background.updateFromObject({
        details: {
          type: BackgroundTypeName.COLOR,
          fill: {
            fillType: getColorFillTypeForBackgroundDropdownItem(clickedId),
            fillColor: getFillColorForBackgroundDropdownItem(clickedId),
          },
        },
      });
      return;
    }

    if (clickedId === PageBackgroundDropDownItem.STOCK_IMAGE) {
      onOpenStockBackgroundModal();
      return;
    }
    if (clickedId === PageBackgroundDropDownItem.MY_VIDEOS) {
      onOpenUserVideoBackgroundUploadsModal();
      return;
    }
    if (clickedId === PageBackgroundDropDownItem.STOCK_VIDEO) {
      onOpenStockVideoBackgroundModal();
      return;
    }
    if (clickedId === PageBackgroundDropDownItem.MY_IMAGES) {
      onOpenUserImageBackgroundUploadsModal();
      return;
    }
    if (clickedId === PageBackgroundDropDownItem.MY_BACKGROUNDS) {
      onOpenUserBackgroundUploadsModal();
    }
  };

  const isBackgroundTypeColor = (id: PageBackgroundDropDownItem): boolean => {
    switch (id) {
      case PageBackgroundDropDownItem.LINEAR_GRADIENT:
      case PageBackgroundDropDownItem.RADIAL_GRADIENT:
      case PageBackgroundDropDownItem.SOLID:
        return true;
      default:
        return false;
    }
  };

  const getSelectorIcon = (): string => {
    switch (getBackgroundDropdownItemForBackgroundObject()) {
      case PageBackgroundDropDownItem.SOLID:
        return 'icon-fill';
      case PageBackgroundDropDownItem.LINEAR_GRADIENT:
        return 'icon-gradient-linear';
      case PageBackgroundDropDownItem.RADIAL_GRADIENT:
        return 'icon-gradient-radial';
      case PageBackgroundDropDownItem.TRANSPARENT:
        return 'icon-transparent';
      case PageBackgroundDropDownItem.MY_BACKGROUNDS:
      case PageBackgroundDropDownItem.MY_IMAGES:
      case PageBackgroundDropDownItem.STOCK_IMAGE:
        return 'icon-image';
      case PageBackgroundDropDownItem.MY_VIDEOS:
        return 'icon-video';
      case PageBackgroundDropDownItem.STOCK_VIDEO:
        return 'icon-stock-video';
      default:
        return '';
    }
  };

  const getSelectorText = (): string => {
    switch (getBackgroundDropdownItemForBackgroundObject()) {
      case PageBackgroundDropDownItem.SOLID:
        return window.i18next.t('pmwjs_solid_color');
      case PageBackgroundDropDownItem.LINEAR_GRADIENT:
        return window.i18next.t('pmwjs_linear_gradient_background');
      case PageBackgroundDropDownItem.RADIAL_GRADIENT:
        return window.i18next.t('pmwjs_radial_gradient_background');
      case PageBackgroundDropDownItem.TRANSPARENT:
        return window.i18next.t('pmwjs_transparent');
      case PageBackgroundDropDownItem.MY_BACKGROUNDS:
      case PageBackgroundDropDownItem.MY_IMAGES:
      case PageBackgroundDropDownItem.STOCK_IMAGE:
        return window.i18next.t('pmwjs_image');
      case PageBackgroundDropDownItem.MY_VIDEOS:
        return window.i18next.t('pmwjs_video');
      case PageBackgroundDropDownItem.STOCK_VIDEO:
        return window.i18next.t('pmwjs_stock_videos');
      default:
        return '';
    }
  };

  const getGradientDropdown = (): ReactElement => {
    return (
      <PageBackgroundFlyoutOption
        id="gradient-background-option"
        text={window.i18next.t('pmwjs_background_gradient')}
        icon="icon-gradient"
        items={[
          {
            id: 'gradient-linear',
            icon: 'icon-gradient-linear',
            text: window.i18next.t('pmwjs_gradient_linear'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.LINEAR_GRADIENT);
            },
          },
          {
            id: 'gradient-radial',
            icon: 'icon-gradient-radial',
            text: window.i18next.t('pmwjs_gradient_radial'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.RADIAL_GRADIENT);
            },
          },
        ]}
      />
    );
  };

  const getImageDropdown = (): ReactElement => {
    return (
      <PageBackgroundFlyoutOption
        id="image-background-option"
        text={window.i18next.t('pmwjs_image')}
        icon="icon-image"
        items={[
          {
            id: 'image-uploads',
            icon: 'icon-upload',
            text: window.i18next.t('pmwjs_uploads'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.MY_IMAGES);
            },
          },
          {
            id: 'image-stock-photos',
            icon: 'icon-stock-image',
            text: window.i18next.t('pmwjs_stock_photo'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.STOCK_IMAGE);
            },
          },
        ]}
      />
    );
  };

  const getVideoDropdown = (): ReactElement => {
    return (
      <PageBackgroundFlyoutOption
        id="video-background-option"
        text={window.i18next.t('pmwjs_video')}
        icon="icon-video"
        items={[
          {
            id: 'video-uploads',
            icon: 'icon-upload',
            text: window.i18next.t('pmwjs_uploads'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.MY_VIDEOS);
            },
          },
          {
            id: 'video-stock-videos',
            icon: 'icon-stock-video',
            text: window.i18next.t('pmwjs_stock_videos'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            onClick: (): void => {
              onClickHandler(PageBackgroundDropDownItem.STOCK_VIDEO);
            },
          },
        ]}
      />
    );
  };

  const getCustomPopup = (): ReactElement => {
    const initialItems: ControlledListItem7[] = [
      {
        id: 'solid-color',
        icon: 'icon-fill',
        text: window.i18next.t('pmwjs_solid_color'),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      },
    ];
    const latterItems: ControlledListItem7[] = [
      {
        id: 'transparent',
        icon: 'icon-transparent',
        text: window.i18next.t('pmwjs_transparent'),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      },
    ];

    return (
      <ul className={`${styles.popup} spacing-m-0`}>
        <ControlledList
          className="spacing-m-l-0 spacing-m-b-2 spacing-m-t-0"
          items={initialItems}
          onClick={(): void => {
            onClickHandler(PageBackgroundDropDownItem.SOLID);
          }}
        />
        {getGradientDropdown()}
        {getImageDropdown()}
        {getVideoDropdown()}
        <ControlledList
          className="spacing-m-l-0 spacing-m-b-0"
          items={latterItems}
          onClick={(): void => {
            onClickHandler(PageBackgroundDropDownItem.TRANSPARENT);
          }}
        />
      </ul>
    );
  };

  return (
    <PosterEditingBaseDropdownOption
      id="design-background-dropdown"
      label={window.i18next.t('pmwjs_background')}
      onClick={onClickHandler}
      hasDefaultSpacing
      customPopup={getCustomPopup()}
      popUpHasCustomWidth
      popupWidth={195}
      position={DROPDOWN_POSITION.FIXED}
      selectorIconClassName={styles.icon}
      selectorIcon={getSelectorIcon()}
      dropdownSelectorText={getSelectorText()}
      onOpen={(): void => {
        trackPosterBuilderGA4Events(GA4EventName.BACKGROUND_MENU_OPEN, {[GA4EventParamName.SOURCE]: GA4EventSources.CONTEXTUAL_MENU});
      }}
    >
      {getSubOptionForBackgroundOption()}
    </PosterEditingBaseDropdownOption>
  );
}
